import TaxonomyCard from '@/components/public/TaxonomyCard'
import { formatBRL, type Pricing } from '@/components/explore/PriceSummary'
import type { Category } from './types'

/**
 * Passo 1 — o que o negócio faz.
 *
 * Todas as modalidades vêm abertas, e é o SERVIÇO marcado que puxa a
 * modalidade para o plano. Não há passo de "escolher categoria antes": ele
 * obrigava dois cliques para dizer uma coisa só e escondia justamente o que a
 * pessoa precisa ver para escolher.
 */
export default function StepServices({
  categories,
  subcategoryIds,
  pricing,
  onToggle,
}: {
  categories: Category[]
  subcategoryIds: number[]
  pricing: Pricing
  onToggle: (id: number) => void
}) {
  return (
    <div>
      <p className="max-w-2xl text-sm text-gray-600">
        Marque tudo que você oferece. O primeiro custa{' '}
        {formatBRL(pricing.first_service)}/mês e cada um a mais,{' '}
        {formatBRL(pricing.extra_service)}/mês.
      </p>

      <div className="mt-6 space-y-4">
        {categories.map((category) => {
          const chosenHere = subcategoryIds.filter((id) =>
            category.subcategories.some((s) => s.id === id),
          )
          const active = chosenHere.length > 0

          return (
            <section
              key={category.id}
              aria-labelledby={`modalidade-${category.id}`}
              className={`overflow-hidden rounded-2xl border transition-colors duration-200 ${
                active ? 'border-primary' : 'border-gray-200'
              }`}
            >
              <header
                className={`flex items-center gap-3 px-4 py-3 transition-colors duration-200 ${
                  active ? 'bg-primary/5' : 'bg-gray-50'
                }`}
              >
                {category.image_path && (
                  <img
                    src={category.image_path}
                    alt=""
                    className="size-12 shrink-0 rounded-xl object-cover"
                  />
                )}

                <div className="min-w-0 flex-1">
                  <p
                    id={`modalidade-${category.id}`}
                    className="text-sm font-bold text-gray-900"
                  >
                    {category.label}
                  </p>
                  <p className="text-xs text-gray-500">
                    {category.subcategories.length}{' '}
                    {category.subcategories.length === 1
                      ? 'opção disponível'
                      : 'opções disponíveis'}
                  </p>
                </div>

                {/* A categoria não tem preço: quem cobra é o serviço. Aqui só
                    o quanto já foi marcado dentro dela. */}
                {chosenHere.length > 0 && (
                  <p className="shrink-0 text-sm font-bold text-primary">
                    {chosenHere.length}{' '}
                    {chosenHere.length === 1 ? 'marcado' : 'marcados'}
                  </p>
                )}
              </header>

              {/* Cards com foto, e não chips: aqui a pessoa está dizendo o que o
                  negócio dela É, e a foto explica isso mais rápido que o
                  rótulo. */}
              <div className="flex flex-wrap gap-3 p-4">
                {category.subcategories.map((sub) => (
                  <TaxonomyCard
                    key={sub.id}
                    label={sub.label}
                    // Serviço sem arte própria cai na foto da categoria — um
                    // quadrado cinza no meio da fileira lê como imagem
                    // quebrada.
                    imageSrc={sub.image_path ?? category.image_path}
                    selected={chosenHere.includes(sub.id)}
                    onToggle={() => onToggle(sub.id)}
                  />
                ))}
              </div>
            </section>
          )
        })}
      </div>
    </div>
  )
}
